import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import axios, { AxiosError } from 'axios';
import toastr from 'toastr';
import { FirmUser } from './FirmUserAccessPage';
import { formatFirmName } from './firmName';
import { REVOCATION_REASONS } from './firmUserAccessLog';

type Props = {
  distro: { id: string; name: string; healthClaimDistroId: string };
  user: FirmUser;
  onClose: (changed: boolean) => void;
};

const RevokeFirmAccessModal = ({ distro, user, onClose }: Props) => {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const revoke = async () => {
    setSaving(true);
    try {
      await axios.post('/api/Advisors/revokedistroaccess', { distroUserId: user.id, reason, note: note.trim() });
      toastr.success(`Access removed for ${user.email} from ${distro.name}.`);
      onClose(true);
    } catch (e) {
      const err = e as AxiosError;
      toastr.error(typeof err.response?.data === 'string' ? err.response.data : `Failed to remove access for ${user.email}.`);
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={() => onClose(false)} centered>
      <Modal.Header closeButton>
        <div>
          <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>Remove access</Modal.Title>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{user.email}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>{formatFirmName(distro)}</div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="danger" style={{ fontSize: 13 }}>
          <strong>Takes effect immediately.</strong> {user.isClientOnly ? 'The user will lose access to this firm in the advisor portal.' : 'The user will lose access to this firm in the advisor portal, including commission statements.'}
        </Alert>
        <div className="mb-3 p-3 rounded" style={{ background: '#f5f5f4', fontSize: 13 }}>
          <div className="d-flex justify-content-between pb-1 mb-1 border-bottom"><span className="text-muted">Email</span><strong>{user.email}</strong></div>
          <div className="d-flex justify-content-between"><span className="text-muted">Scope</span><span>Entire Firm · {user.accessTypeLabel}</span></div>
        </div>
        <Form.Group className="mb-3">
          <Form.Label>Reason for revocation <span className="text-danger">*</span></Form.Label>
          <Form.Select value={reason} onChange={e => setReason(e.target.value)}>
            <option value="">Select a reason…</option>
            {REVOCATION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Ticket number or comment <span className="text-danger">*</span></Form.Label>
          <Form.Control value={note} maxLength={500} onChange={e => setNote(e.target.value)} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onClose(false)}>Cancel</Button>
        <Button variant="danger" disabled={reason === '' || note.trim() === '' || saving} onClick={revoke}>Remove immediately</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RevokeFirmAccessModal;
